/**
 * AuthService — Authentication Logic
 *
 * - Admin role is determined by database `profiles.role` column — NEVER hardcoded
 * - All passwords go through bcrypt (via PostgreSQL crypt function)
 * - Google OAuth uses Supabase in production mode
 * - Local mode: PostgREST + JWT session via ApiClient
 */

import { ApiClient } from './ApiClient';
import { AuthStore, UserProfile, AVATARS_LIST } from '../core/store/authStore';
import { EventBus } from '../core/events/EventBus';
import { ToastManager } from '../ui/components/Toast';
import { FriendService } from './FriendService';
import { DuelService } from './DuelService';

export class AuthService {
  static async initAuth(): Promise<void> {
    if (ApiClient.isSupabase) {
      const { supabase } = await import('./supabase');
      supabase.auth.onAuthStateChange(async (event, session) => {
        if (session?.user) {
          const { data } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
          if (data) {
            const profile = this._mapDbUser(data);
            AuthStore.setUser(profile);
            FriendService.initPresence();
            DuelService.init();
            EventBus.emit('auth:login', { userId: profile.id, username: profile.username });
          }
        } else {
          AuthStore.setUser(null);
          FriendService.disconnectPresence();
          DuelService.disconnect();
        }
      });
    }
  }

  /**
   * Email + password login
   * Admin check: DB role field only — no hardcoded email list
   */
  static async login(email: string, pass: string): Promise<boolean> {
    const { data, error } = await ApiClient.login(email.trim(), pass);

    if (error || !data) {
      // Show a user-friendly error (never expose internal codes)
      const msg = error?.includes('bulunamadı')  ? 'E-posta adresi bulunamadı.'
                : error?.includes('Şifre')        ? 'Şifre yanlış.'
                : error?.includes('Bağlantı')     ? '⚠️ Sunucuya bağlanılamadı. Docker çalışıyor mu?'
                : 'Giriş başarısız. Lütfen tekrar deneyin.';
      ToastManager.show(msg, 'error');
      return false;
    }

    const profile = this._mapDbUser(data);
    AuthStore.setUser(profile);
    FriendService.initPresence();
    DuelService.init();
    EventBus.emit('auth:login', { userId: profile.id, username: profile.username });
    ToastManager.show(
      `Hoş geldin, ${profile.username}${profile.tag}! ${profile.role === 'admin' ? '👑 Yönetici' : '🎮 Oyuncu'}`,
      'success',
    );
    return true;
  }

  /**
   * Register new account
   * Role is always 'user' — admin is set manually from DB
   */
  static async register(email: string, pass: string, username: string): Promise<boolean> {
    if (pass.length < 8) {
      ToastManager.show('Şifre en az 8 karakter olmalı.', 'error');
      return false;
    }
    if (username.length < 3) {
      ToastManager.show('Kullanıcı adı en az 3 karakter olmalı.', 'error');
      return false;
    }

    const { data, error } = await ApiClient.register(email.trim(), pass, username.trim());

    if (error || !data) {
      const msg = error?.includes('zaten kayıtlı')    ? 'Bu e-posta adresi zaten kullanılıyor.'
                : error?.includes('kullanıcı adı')    ? 'Bu kullanıcı adı alınmış. Farklı bir ad dene.'
                : error?.includes('Bağlantı')         ? '⚠️ Sunucuya bağlanılamadı. Docker çalışıyor mu?'
                : 'Kayıt başarısız. Lütfen tekrar deneyin.';
      ToastManager.show(msg, 'error');
      return false;
    }

    const profile = this._mapDbUser(data);
    AuthStore.setUser(profile);
    EventBus.emit('auth:login', { userId: profile.id, username: profile.username });
    ToastManager.show(`🎮 Hoş geldin ${username}${profile.tag}! Hesabın oluşturuldu.`, 'success');
    return true;
  }

  /**
   * Google OAuth
   * - Production (Supabase): real redirect
   * - Local (Docker):        shows info toast
   */
  static async loginWithGoogle(): Promise<void> {
    const { error } = await ApiClient.loginWithGoogle();

    if (error) {
      if (ApiClient.isLocal) {
        ToastManager.show(
          '🌐 Google girişi yalnızca canlı (production) modunda çalışır. Lütfen e-posta ile giriş yapın.',
          'info',
        );
      } else {
        ToastManager.show(`Google ile giriş başarısız: ${error}`, 'error');
      }
      return;
    }

    // Supabase redirects the browser — no further action needed here
    ToastManager.show('Google doğrulama sayfasına yönlendiriliyorsunuz...', 'info');
  }

  static async logout(): Promise<void> {
    ApiClient.logout();
    AuthStore.setUser(null);
    FriendService.disconnectPresence();
    DuelService.disconnect();
    EventBus.emit('auth:logout', {});
    ToastManager.show('Oturum kapatıldı.', 'info');
  }

  // ─── Private Helpers ─────────────────────────────────────────────────────

  private static _mapDbUser(data: {
    id: string;
    username: string;
    email: string;
    role: 'user' | 'admin';
    player_tag: string;
    avatar_id: string;
    duels_won?: number;
    win_streak?: number;
  }): UserProfile {
    // Map avatar_id -> emoji icon (fallback to robot)
    const avatarEntry = AVATARS_LIST.find((a) => a.id === data.avatar_id) ?? AVATARS_LIST[2];

    return {
      id:        data.id,
      username:  data.username,
      email:     data.email,
      role:      data.role,
      tag:       data.player_tag,
      avatarUrl: avatarEntry.icon,
      avatarId:  data.avatar_id,
      duels_won: data.duels_won || 0,
      win_streak: data.win_streak || 0,
    };
  }
}

